import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "../Container";

function ActionLink({ action, className, children }) {
  if (!action?.label || !action?.url) return null;
  const content = children || action.label;

  if (/^https?:\/\//.test(action.url)) {
    return <a href={action.url} className={className}>{content}</a>;
  }

  return <Link to={action.url} className={className}>{content}</Link>;
}

function HeroSection({ section }) {
  return (
    <section className="hero-shell">
      <div
        className="relative min-h-[min(680px,78vh)] overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url("${section.image}")` }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(15,18,14,0.08),rgba(15,18,14,0.34)_48%,rgba(15,18,14,0.82))]" />
        <div className="relative mx-auto flex min-h-[min(680px,78vh)] w-full max-w-[1240px] items-end px-5 pb-16 pt-24 sm:px-8 lg:pb-20">
          <div className="max-w-2xl text-right text-white">
            {section.eyebrow && <p className="mb-4 text-sm font-bold text-[#d6c49e]">{section.eyebrow}</p>}
            <h1 className="whitespace-pre-line text-[42px] font-extrabold leading-[1.28] sm:text-[56px] lg:text-[68px]">{section.title}</h1>
            {section.text && <p className="mt-5 max-w-xl text-base leading-8 text-white/85">{section.text}</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              <ActionLink action={section.primaryAction} className="bg-[#b99758] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#a17d3d]" />
              <ActionLink action={section.secondaryAction} className="border border-white/65 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white hover:text-[#252923]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LinkGridSection({ section }) {
  return (
    <section className="bg-[#faf9f6] py-16 sm:py-20" id={section.anchor || undefined}>
      <Container>
        <div className="mb-10 border-b border-[#dcd7cd] pb-6">
          {section.eyebrow && <p className="text-sm font-bold text-[#9a763d]">{section.eyebrow}</p>}
          <h2 className="mt-2 text-[34px] font-extrabold text-[#252923] sm:text-[42px]">{section.title}</h2>
          {section.text && <p className="mt-3 max-w-2xl leading-7 text-[#686b65]">{section.text}</p>}
        </div>
        <div className="grid border-l border-t border-[#dcd7cd] md:grid-cols-2 lg:grid-cols-3">
          {(section.items || []).map((item, index) => (
            <ActionLink
              key={`${item.url}-${index}`}
              action={item}
              className="group min-h-[250px] border-b border-r border-[#dcd7cd] bg-white p-7 transition-colors hover:bg-[#f4f1ea] sm:p-8"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="text-xs font-bold text-[#9a763d]">{item.eyebrow}</span>
                <span className="text-sm text-[#a6a69f]">{String(index + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-7 text-2xl font-bold text-[#252923]">{item.label}</h3>
              <p className="mt-3 line-clamp-3 text-sm leading-7 text-[#686b65]">{item.description}</p>
              <span className="mt-6 inline-block text-lg text-[#9a763d] transition-transform group-hover:-translate-x-1" aria-hidden="true">←</span>
            </ActionLink>
          ))}
        </div>
      </Container>
    </section>
  );
}

function ContentSection({ section }) {
  const imageFirst = section.imagePosition === "start";
  return (
    <section className="bg-white py-16 sm:py-20" id={section.anchor || undefined}>
      <Container>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className={imageFirst ? "lg:order-2" : ""}>
            {section.eyebrow && <p className="text-sm font-bold text-[#9a763d]">{section.eyebrow}</p>}
            <h2 className="mt-2 text-[34px] font-extrabold text-[#252923] sm:text-[42px]">{section.title}</h2>
            <p className="mt-5 whitespace-pre-line leading-8 text-[#686b65]">{section.text}</p>
            <div className="mt-7"><ActionLink action={section.action} className="inline-block bg-[#252923] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#9a763d]" /></div>
          </div>
          {section.image && (
            <img className={`aspect-[4/3] w-full object-cover ${imageFirst ? "lg:order-1" : ""}`} src={section.image} alt={section.imageAlt || ""} />
          )}
        </div>
      </Container>
    </section>
  );
}

function getYouTubeId(url = "") {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return match?.[1] || "";
}

function MediaIcon({ type }) {
  return type === "video" ? (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true"><path d="M8 5.5v13l10-6.5L8 5.5Z" /></svg>
  ) : (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth="1.8" aria-hidden="true"><path d="M4 5h16v14H4z"/><path d="m4 16 4.5-4.5 3 3 2-2 6.5 6.5"/><circle cx="15.5" cy="9" r="1.5"/></svg>
  );
}

function NewsSection({ section }) {
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const items = (section.items || []).slice(0, 3);

  useEffect(() => {
    if (!activeAlbum) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setActiveAlbum(null);
      if (event.key === "ArrowLeft") setPhotoIndex((value) => (value + 1) % activeAlbum.length);
      if (event.key === "ArrowRight") setPhotoIndex((value) => (value - 1 + activeAlbum.length) % activeAlbum.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeAlbum]);

  const openAlbum = (photos) => {
    if (!photos?.length) return;
    setPhotoIndex(0);
    setActiveAlbum(photos);
  };

  return (
    <section className="overflow-hidden bg-[#faf9f6] py-16 sm:py-20" id={section.anchor || undefined}>
      <Container>
        <div className="mb-10 flex flex-col gap-5 border-b border-[#dcd7cd] pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            {section.eyebrow && <p className="text-sm font-bold text-[#9a763d]">{section.eyebrow}</p>}
            <h2 className="mt-2 text-[34px] font-extrabold leading-tight text-[#252923] sm:text-[42px]">{section.title}</h2>
          </div>
          {section.text && <p className="max-w-md text-sm leading-7 text-[#686b65] sm:text-left">{section.text}</p>}
        </div>

        <div className="flex flex-col gap-6">
          {items.map((item, index) => {
            const videoId = getYouTubeId(item.youtubeUrl);
            const photos = item.photos || [];
            const cover = item.coverImage || photos[0]?.image;
            const isVideo = item.mediaType === "video" && videoId;
            const hasArticle = Boolean(item.slug);
            return (
              <article key={`${item.title}-${index}`} className="group grid overflow-hidden border border-[#dcd7cd] bg-white md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                <div className="relative aspect-video overflow-hidden bg-black md:aspect-auto md:min-h-72">
                  {isVideo ? (
                    <iframe className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${videoId}`} title={item.title} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                  ) : cover ? (
                    <button type="button" onClick={() => openAlbum(photos)} className="block h-full w-full text-right" aria-label={`فتح ألبوم ${item.title}`}>
                      <img src={cover} alt={item.imageAlt || item.title} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.035]" loading="lazy" />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                      {photos.length > 1 && <span className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/65 px-3 py-2 text-xs font-bold backdrop-blur-sm"><MediaIcon type="album" />{photos.length} صور</span>}
                    </button>
                  ) : <div className="h-full w-full bg-[#f0ede6]" />}
                </div>
                <div className="flex flex-col justify-center p-6 sm:p-8">
                  <div className="flex items-center gap-3 text-xs font-bold text-[#9a763d]">
                    <span className="flex items-center gap-1.5"><MediaIcon type={isVideo ? "video" : "album"} />{isVideo ? "فيديو" : "ألبوم صور"}</span>
                    {item.date && <><span className="h-1 w-1 rounded-full bg-[#b9b5ac]" /><time>{item.date}</time></>}
                  </div>
                  <h3 className="mt-3 text-2xl font-bold leading-[1.55] text-[#252923] sm:text-[30px]">
                    {hasArticle ? <Link to={`/news/${item.slug}`} className="transition-colors hover:text-[#9a763d]">{item.title}</Link> : item.title}
                  </h3>
                  {item.summary && <p className="mt-3 line-clamp-2 text-sm leading-7 text-[#686b65]">{item.summary}</p>}
                  {hasArticle && <Link to={`/news/${item.slug}`} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#8d6b35]">قراءة الخبر <span aria-hidden="true">←</span></Link>}
                </div>
              </article>
            );
          })}
        </div>
        <div className="mt-9 border-t border-[#dcd7cd] pt-6 text-center">
          <Link to="/news" className="inline-block border border-[#252923] px-6 py-3 text-sm font-bold text-[#252923] transition-colors hover:bg-[#252923] hover:text-white">جميع الأخبار</Link>
        </div>
      </Container>

      {activeAlbum && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-3 sm:p-8" role="dialog" aria-modal="true" aria-label="عارض الصور" onClick={() => setActiveAlbum(null)}>
          <button className="absolute left-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl text-white hover:bg-white/20" onClick={() => setActiveAlbum(null)} aria-label="إغلاق">×</button>
          <div className="relative flex h-full w-full max-w-6xl items-center justify-center" onClick={(event) => event.stopPropagation()}>
            <img src={activeAlbum[photoIndex].image} alt={activeAlbum[photoIndex].alt || ""} className="max-h-full max-w-full object-contain" />
            {activeAlbum.length > 1 && <>
              <button className="absolute right-1 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-3xl text-white hover:bg-black/75 sm:right-4" onClick={() => setPhotoIndex((photoIndex - 1 + activeAlbum.length) % activeAlbum.length)} aria-label="الصورة السابقة">›</button>
              <button className="absolute left-1 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-3xl text-white hover:bg-black/75 sm:left-4" onClick={() => setPhotoIndex((photoIndex + 1) % activeAlbum.length)} aria-label="الصورة التالية">‹</button>
              <span className="absolute bottom-2 rounded-full bg-black/60 px-4 py-2 text-sm text-white sm:bottom-4">{photoIndex + 1} / {activeAlbum.length}</span>
            </>}
          </div>
        </div>
      )}
    </section>
  );
}

const renderers = { hero: HeroSection, link_grid: LinkGridSection, content: ContentSection, news: NewsSection };

export default function HomepageSections({ sections }) {
  return sections.filter((section) => !section.hidden).map((section, index) => {
    const Section = renderers[section.type];
    return Section ? <Section key={section.id || `${section.type}-${index}`} section={section} /> : null;
  });
}
